import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Award, Wine, Leaf, Cigarette } from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../services/api';
import { toast } from 'sonner';

const cardTypeConfig = {
  alcohol: {
    name: 'Alcool',
    icon: Wine,
    color: '#722F37',
    bgColor: '#F3E5E7'
  },
  cbd: {
    name: 'CBD',
    icon: Leaf,
    color: '#2D5A45',
    bgColor: '#E8F5E9'
  },
  puffs: {
    name: 'Puffs',
    icon: Cigarette,
    color: '#6366F1',
    bgColor: '#EEF2FF'
  }
};

export default function HistoryPage() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const data = await api.getCardsHistory();
      setHistory(data);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen overflow-x-hidden overflow-y-auto flex items-center justify-center">
        <div className="text-xl font-semibold">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden overflow-y-auto p-6 sm:p-8" style={{ background: 'linear-gradient(135deg, #0a0015 0%, #1a0030 50%, #0a0015 100%)' }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-6xl mx-auto mb-8"
      >
        <Button
          onClick={() => navigate('/team-lpe/search')}
          variant="outline"
          className="mb-4 text-cyan-400 border-cyan-500 hover:bg-cyan-500/20"
          data-testid="back-to-search-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Retour à la recherche
        </Button>

        <div 
          className="backdrop-blur-xl rounded-3xl p-6 sm:p-8 border-2"
          style={{
            background: 'rgba(10, 0, 30, 0.8)',
            borderColor: '#D4AF37',
            boxShadow: '0 0 30px rgba(212, 175, 55, 0.3)'
          }}
        >
          <div className="flex items-center gap-4">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #722F37 0%, #D4AF37 100%)',
                boxShadow: '0 0 20px rgba(212, 175, 55, 0.5)'
              }}
            >
              <Award className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif', textShadow: '0 0 10px rgba(212, 175, 55, 0.5)' }}>
                Tiroir Virtuel
              </h1>
              <p className="text-gray-400">Historique des cartes complétées</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* History List */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="max-w-6xl mx-auto space-y-4"
      >
        {history.length === 0 ? (
          <div 
            className="backdrop-blur-xl rounded-2xl p-12 text-center border-2"
            style={{
              background: 'rgba(10, 0, 30, 0.7)',
              borderColor: '#333'
            }}
            data-testid="empty-history"
          >
            <Award className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-xl text-gray-400">Aucune carte complétée pour le moment</p>
          </div>
        ) : (
          history.map((card, index) => {
            const config = cardTypeConfig[card.type];
            const Icon = config.icon;
            
            return (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="backdrop-blur-xl rounded-2xl p-6 border-2"
                style={{ 
                  background: 'rgba(10, 0, 30, 0.7)',
                  borderColor: config.color,
                  boxShadow: `0 0 20px ${config.color}40`
                }}
                data-testid={`history-card-${index}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center"
                      style={{ 
                        background: `${config.color}30`,
                        boxShadow: `0 0 15px ${config.color}50`
                      }}
                    >
                      <Icon className="w-7 h-7" style={{ color: config.color }} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        Carte {config.name}
                      </h3>
                      <p className="text-sm text-gray-500 mono">{card.id.substring(0, 20)}...</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 text-gray-300 mb-1">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">
                        {new Date(card.completedAt).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(card.completedAt).toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </motion.div>
    </div>
  );
}